"use client";

import { CaretDownIcon, type IconProps } from "@phosphor-icons/react";
import {
  GearIcon,
  ListIcon,
  QuestionIcon,
  SignOutIcon,
  UserIcon,
} from "@phosphor-icons/react/dist/ssr";
import { get } from "lodash";
import Image from "next/image"; // Import Image component
import { useState } from "react";
import { LanguageSwitcherMenuItem } from "@/components/common/language-switcher";
import { LocalizedLink } from "@/components/common/localized-link/localized-link";
import SupportMenuItem from "@/components/layout/sidebar/components/support-menu-item/support-menu-item";
import Sidebar from "@/components/layout/sidebar/sidebar";
import { SignOutDialog } from "@/components/layout/sidebar/sign-out-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"; // Import Avatar
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Typography } from "@/components/ui/typography";
import useCurrentUserManager from "@/hooks/use-current-user-manager";
import { usePaidFeatureExclusion } from "@/hooks/use-paid-feature-exclusion";
import { useTranslation } from "@/i18n/client";
import useBoundStore from "@/store/bound-store"; // Import zustand store hook
import usePersistedBoundStore from "@/store/persisted-bound-store"; // Import persisted store
import { getSignOutRedirectUri } from "@/utils/auth";
import { cn } from "@/utils/shared";
import { getInitials } from "@/utils/string-utils"; // Import from utils
import { clearSignoutTransitionState } from "@/utils/tab-session";

interface ProfileDropdownMenuItem {
  path: string;
  label: string;
  icon: React.ForwardRefExoticComponent<IconProps & React.RefAttributes<SVGSVGElement>>;
}

const TopBar = () => {
  const person = useBoundStore.use.person(); // Get person data from zustand store
  const realm = usePersistedBoundStore.use.realm(); // Get current realm
  const currentUserManager = useCurrentUserManager();
  const { t: tSidebar } = useTranslation("sidebar");
  const environment = usePersistedBoundStore.use.environment();
  const { isPaidFeatureExcluded } = usePaidFeatureExclusion();

  // Check if user is in evbasic realm (controlled basic process)
  const isEvbasicUser = realm === "evbasic";

  // Additional variables for dropdown
  const urlAvatar = get(person, "urlAvatar", null);
  const isControlledProcess = realm === "evbasic";

  // Logout functionality state
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [isSignOutDialogOpen, setIsSignOutDialogOpen] = useState(false);

  // Dropdown state
  const [, setIsDropdownOpen] = useState(false);

  // Avatar image state
  const [isAvatarImageLoaded, setIsAvatarImageLoaded] = useState(false);

  const displayName = (() => {
    const nameParts = person?.firstName?.split(" ").slice(0, 2) || [];
    return nameParts.join(" ").toLowerCase() || person?.identification;
  })();

  // Dropdown menu items
  const dropdownMenuProfile: ProfileDropdownMenuItem[] = isControlledProcess
    ? []
    : [
        {
          path: "/settings",
          label: tSidebar("accountSettings"),
          icon: GearIcon,
        },
        {
          path: "/settings/billing",
          label: tSidebar("subscription"),
          icon: UserIcon,
        },
      ];

  const visibleDropdownMenuProfile = dropdownMenuProfile.filter((item) => {
    if (!isPaidFeatureExcluded) {
      return true;
    }

    return item.path !== "/settings/billing";
  });

  const showEnhancePlanItem = !isControlledProcess && !isPaidFeatureExcluded;

  // Get avatar initials based on user type
  const getAvatarInitials = () => {
    if (isEvbasicUser) {
      // For evbasic users, use last 2 digits of identification
      const identification = person?.identification || "";
      if (identification) {
        return identification.slice(-2).padStart(2, "0");
      }
      return "00"; // Fallback for evbasic users without identification
    }
    // For regular users, use first name and last name initials
    return getInitials(person?.firstName, person?.lastName);
  };

  // Get display name based on user type
  const getDisplayName = () => {
    if (isEvbasicUser) {
      return person?.identification || "Usuario Básico";
    }
    return displayName || "Usuario";
  };

  // Get display email/identification based on user type
  const getDisplayEmailOrId = () => {
    if (isEvbasicUser) {
      return person?.identification || "Sin identificación";
    }
    return person?.mail || "";
  };

  // Check if avatar image is available
  const hasAvatarImage = Boolean(person?.urlAvatar);

  // Logout handlers
  const handleSignOut = async () => {
    try {
      setIsSigningOut(true);
      clearSignoutTransitionState();
      const redirectUri = getSignOutRedirectUri(environment);
      await currentUserManager?.signoutRedirect(
        redirectUri ? { post_logout_redirect_uri: redirectUri } : undefined
      );
    } catch {
      // Sign out errors are handled by finally block
    } finally {
      setIsSigningOut(false);
    }
  };

  const openSignOutDialog = () => {
    setIsSignOutDialogOpen(true);
  };

  const closeSignOutDialog = () => {
    setIsSignOutDialogOpen(false);
  };

  return (
    <nav
      className={cn(
        "bg-neutral-100 max-md:border-b max-md:border-primary-200 md:hidden sm:shadow-none z-40 p-4",
        " md:h-auto h-[56px] fixed md:absolute md:justify-end md:top-3",
        "lg:top-4.5 top-0 flex items-center justify-between max-w-[1600px] w-full mx-auto"
      )}
    >
      <div className="md:hidden flex items-center gap-5">
        <Sheet>
          <SheetTrigger>
            <ListIcon size={24} className="text-neutral-500" />
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-[240px]">
            <SheetTitle className="sr-only">Menu de navegación</SheetTitle>
            <SheetDescription className="sr-only">
              Panel de navegación principal con acceso a procesos, perfil y configuraciones
            </SheetDescription>
            <Sidebar isMobileSheet={true} />
          </SheetContent>
        </Sheet>
        <Image
          src="/images/logos/evaluar-isotype.svg"
          alt="Evaluar Logo Isotype"
          width={32}
          height={24}
        />
      </div>
      <div className="flex items-center gap-5">
        <DropdownMenu onOpenChange={setIsDropdownOpen}>
          <DropdownMenuTrigger
            className="md:hidden"
            asChild={true}
            disabled={!isAvatarImageLoaded && hasAvatarImage}
          >
            <Button
              variant="ghost"
              className={cn(
                "flex items-center gap-2 text-left w-full justify-between px-2 py-1.5",
                "hover:bg-primary-50 transition-colors duration-150 rounded-lg h-auto",
                "focus-visible:ring-0 focus-visible:ring-offset-0"
              )}
            >
              <div className="flex items-center gap-2">
                <div
                  className="p-0.5 rounded-full relative"
                  style={{ minWidth: "28px", minHeight: "28px" }}
                >
                  {hasAvatarImage && isAvatarImageLoaded ? (
                    <>
                      <div className="absolute inset-0 rounded-full bg-alba" />
                      <Avatar className="relative w-7 h-7">
                        <AvatarImage
                          src={urlAvatar || undefined}
                          width={28}
                          height={28}
                          onLoad={() => setIsAvatarImageLoaded(true)}
                          onError={() => setIsAvatarImageLoaded(false)}
                        />
                        <AvatarFallback className="bg-green-600 text-white font-semibold text-xs">
                          {getAvatarInitials()}
                        </AvatarFallback>
                      </Avatar>
                    </>
                  ) : (
                    <Avatar className="relative w-7 h-7">
                      <AvatarImage
                        src={urlAvatar || undefined}
                        width={28}
                        height={28}
                        onLoad={() => setIsAvatarImageLoaded(true)}
                        onError={() => setIsAvatarImageLoaded(false)}
                      />
                      <AvatarFallback className="bg-green-600 text-white font-semibold text-xs">
                        {getAvatarInitials()}
                      </AvatarFallback>
                    </Avatar>
                  )}
                </div>
              </div>
              <CaretDownIcon size={16} weight="bold" className="text-neutral-400" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-auto border-none rounded-xl bg-transparent p-0 mb-2"
            align="end"
          >
            <div className="bg-white rounded-xl shadow-lg border border-neutral-200 overflow-hidden min-w-[240px]">
              {/* User Info Section */}
              <div className="px-4 py-3 bg-gradient-to-b from-primary-50/50 to-white">
                <div className="flex items-center gap-3">
                  <div className="p-0.5 rounded-full bg-alba">
                    <Avatar className="w-10 h-10">
                      <AvatarImage
                        src={urlAvatar || undefined}
                        onLoad={() => setIsAvatarImageLoaded(true)}
                        onError={() => setIsAvatarImageLoaded(false)}
                      />
                      <AvatarFallback className="bg-green-600 text-white font-semibold">
                        {getAvatarInitials()}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                  <div className="flex-1 min-w-0">
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild={true}>
                          <Typography
                            variant="label 2"
                            className={cn(
                              "text-neutral-700 truncate block cursor-default",
                              !isEvbasicUser && "capitalize"
                            )}
                          >
                            {getDisplayName()}
                          </Typography>
                        </TooltipTrigger>
                        <TooltipContent
                          side="left"
                          className="bg-neutral-100 border border-primary-200 text-neutral-700 z-[250] max-w-[300px] px-2 py-1"
                        >
                          <Typography
                            variant="label 6"
                            className={cn("break-words", !isEvbasicUser && "capitalize")}
                          >
                            {getDisplayName()}
                          </Typography>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild={true}>
                          <Typography
                            variant="label 5"
                            className="text-neutral-500 font-normal truncate block cursor-default"
                          >
                            {getDisplayEmailOrId()}
                          </Typography>
                        </TooltipTrigger>
                        <TooltipContent
                          side="left"
                          className="bg-neutral-100 border border-primary-200 text-neutral-700 z-[250] max-w-[300px] px-2 py-1"
                        >
                          <Typography variant="label 6" className="break-words">
                            {getDisplayEmailOrId()}
                          </Typography>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              </div>

              <Separator className="bg-neutral-100" />

              {/* Menu Items */}
              <div className="py-1">
                {visibleDropdownMenuProfile.map((item) => {
                  const ItemIcon = item.icon;
                  return (
                    <LocalizedLink key={item.path} href={item.path}>
                      <DropdownMenuItem className="cursor-pointer mx-2 rounded-lg px-3 py-2 focus:bg-primary-50">
                        <ItemIcon size={16} className="mr-3 text-neutral-600" weight="regular" />
                        <Typography variant="label 3" className="text-neutral-700">
                          {item.label}
                        </Typography>
                      </DropdownMenuItem>
                    </LocalizedLink>
                  );
                })}

                {showEnhancePlanItem && (
                  <LocalizedLink href="/plans">
                    <DropdownMenuItem className="cursor-pointer mx-2 rounded-lg px-3 py-2 focus:bg-primary-50">
                      <Image
                        src="/images/shapes/crown-simple.svg"
                        alt="Crown icon"
                        width={16}
                        height={16}
                        className="mr-3 flex-shrink-0"
                      />
                      <Typography variant="label 3" className="text-neutral-700">
                        {tSidebar("enhance-plan")}
                      </Typography>
                    </DropdownMenuItem>
                  </LocalizedLink>
                )}

                <LocalizedLink href="/faq" target="_blank">
                  <DropdownMenuItem className="cursor-pointer mx-2 rounded-lg px-3 py-2 focus:bg-primary-50">
                    <QuestionIcon size={16} className="mr-3 text-neutral-600" weight="regular" />
                    <Typography variant="label 3" className="text-neutral-700">
                      FAQ
                    </Typography>
                  </DropdownMenuItem>
                </LocalizedLink>

                <SupportMenuItem variant="topbar" />
                <div className="mx-2 px-3 py-1">
                  <LanguageSwitcherMenuItem />
                </div>
              </div>

              <Separator className="bg-neutral-100" />

              {/* Sign Out */}
              <div className="py-1">
                <DropdownMenuItem
                  className="cursor-pointer mx-2 rounded-lg px-3 py-2 text-red-600 focus:text-red-600 focus:bg-red-50"
                  onClick={openSignOutDialog}
                  disabled={isSigningOut}
                >
                  <SignOutIcon size={16} className="mr-3" weight="regular" />
                  <Typography variant="label 3">
                    {isSigningOut ? `${tSidebar("signingOut")}...` : tSidebar("logout")}
                  </Typography>
                </DropdownMenuItem>
              </div>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* SignOutDialog */}
      <SignOutDialog
        isOpen={isSignOutDialogOpen}
        onClose={closeSignOutDialog}
        onConfirm={handleSignOut}
        isSigningOut={isSigningOut}
      />
    </nav>
  );
};

export default TopBar;
